import React, { PureComponent } from 'react';
import { connect } from 'react-redux';
import { withTranslation } from 'react-i18next';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import bindMethods from 'auto-bind';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPlusCircle } from '@fortawesome/free-solid-svg-icons';
import { darken } from 'polished';
import Mousetrap from 'mousetrap';

import DownDistance from './downDistance';
import FieldZone from './fieldZone';
import { XOS_RED, XOS_GREEN } from '../../constants/colors';
import { DOWN_AND_DISTANCE_KEY, FIELD_ZONE_KEY } from '../../state/selectors/filters';
import { clearView, setActiveTab } from '../../state/settings';
import { addFilters } from '../../state/filters';
import { DOWN_AND_DISTANCE, FIELD_ZONE } from '../../constants/filters';

const ModalContainer = styled.div`
  display: ${props => (props.active ? 'block' : 'none')};
  position: fixed;
  z-index: 1000;
  left: 0;
  top: 0;
  width: 100%;
  height: 100%;
  overflow: auto;
  background-color: rgba(0, 0, 0, 0.4);
`;

const ModalContent = styled.div`
  background-color: #fefefe;
  margin: 15% auto;
  padding: 20px;
  border: 1px solid #888;
  width: 50%;
`;

const Tabs = styled.div`
  display: flex;
  overflow: hidden;
  background-color: inherit;
  border-bottom: 2px solid ${XOS_RED};
  align-items: center;
`;

const TabButtons = styled.div`
  background-color: ${props => (props.active ? XOS_RED : '#000000')};
  display: flex;
  justify-content: center;
  align-items: center;
  color: #fbfbfb;
  float: left;
  width: 180px;
  height: 40px;
  border: none;
  outline: none;
  cursor: pointer;
  padding: 14px 16px;
  transition: 0.3s;
  font-size: 17px;

  :hover {
    font-weight: ${props => (props.active ? 'none' : '500')};
  }
`;

const TabContent = styled.div`
  display: ${props => (props.active ? 'block' : 'none')};
  padding: 6px 12px;
  border: 1px solid #e0e0e0;
  border-top: none;
`;

const ButtonContainer = styled.div`
  position: relative;
  display: flex;
  justify-content: flex-end;
  color: ${XOS_GREEN};
  flex: 1;
  cursor: pointer;
`;

const ModalFooter = styled.div`
  display: flex;
  padding-top: 20px;
  justify-content: flex-end;
  align-items: center;
`;

const ClosingButton = styled.button`
  display: block;
  width: 110px;
  border: none;
  border-radius: 2px;
  font-size: 16px;
  height: 40px;
  background-color: ${XOS_RED};
  color: #fff;
  cursor: pointer;

  :hover {
    background: ${darken(0.05, XOS_RED)};
  }
`;

class FootballSettingsModal extends PureComponent {
  constructor(props) {
    super(props);
    bindMethods(this);
    this.keyboardShortcut = ['esc'];
  }

  componentDidMount() {
    const { clearView } = this.props;
    Mousetrap.bind(this.keyboardShortcut, clearView);
  }

  componentWillUnmount() {
    Mousetrap.unbind(this.keyboardShortcut);
  }

  addFilter() {
    const {
      filters, addFilters, error, activeTab,
    } = this.props;
    const fieldType = activeTab === DOWN_AND_DISTANCE_KEY ? DOWN_AND_DISTANCE : FIELD_ZONE;

    if (!Object.keys(filters).length > 0) return;

    const containsNewFilterInput = filters[fieldType].length > 0
      ? !Object.keys(filters[fieldType][filters[fieldType].length - 1]).length > 0
      : false;

    if (containsNewFilterInput || error.location === activeTab) return;

    const filtered = {
      ...filters,
      [fieldType]: [...filters[fieldType], {}],
    };
    addFilters(filtered);
  }

  render() {
    const {
      active, clearView, activeTab, setActiveTab, groupId, t,
    } = this.props;
    const isDD = activeTab === DOWN_AND_DISTANCE_KEY;
    const isFZ = activeTab === FIELD_ZONE_KEY;

    return (
      <ModalContainer id="GeneralSettings.FootballModal" active={active}>
        <ModalContent>
          <Tabs>
            <TabButtons active={isDD} onClick={() => setActiveTab(DOWN_AND_DISTANCE_KEY)}>
              {t('NavigationBar.User.Football.DownDistanceTab')}
            </TabButtons>
            <TabButtons active={isFZ} onClick={() => setActiveTab(FIELD_ZONE_KEY)}>
              {t('NavigationBar.User.Football.FieldZoneTab')}
            </TabButtons>
            {groupId && (
              <ButtonContainer>
                <FontAwesomeIcon icon={faPlusCircle} size="2x" onClick={this.addFilter} />
              </ButtonContainer>
            )}
          </Tabs>
          <TabContent active={isDD}>
            <DownDistance />
          </TabContent>
          <TabContent active={isFZ}>
            <FieldZone />
          </TabContent>
          <ModalFooter>
            <ClosingButton id="GeneralSettings.FootballModal.Close" onClick={() => clearView()}>

              Close
            </ClosingButton>
          </ModalFooter>
        </ModalContent>
      </ModalContainer>
    );
  }
}

FootballSettingsModal.propTypes = {
  t: PropTypes.func.isRequired,
  active: PropTypes.bool.isRequired,
  addFilters: PropTypes.func.isRequired,
  groupId: PropTypes.number,
  filters: PropTypes.object.isRequired,
  setActiveTab: PropTypes.func.isRequired,
  activeTab: PropTypes.string.isRequired,
  clearView: PropTypes.func.isRequired,
  error: PropTypes.object.isRequired,
};

const mapStateToProps = state => ({
  error: state.settings.error,
  filters: state.filters,
  activeTab: state.settings.activeTab,
  groupId: state.navigationSelection.group.groupId,
});

const mapDispatchToProps = {
  addFilters,
  clearView,
  setActiveTab,
};

const TranslatedComponent = withTranslation()(FootballSettingsModal);

export default connect(
  mapStateToProps,
  mapDispatchToProps,
)(TranslatedComponent);
